import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Form, Card, CardBody, CardFooter, Col, Container, FormGroup, Input, Label, Row, Table, Button } from "reactstrap";
import { toast } from "react-toastify";

import { confirm } from "components/Confirmation";

import usuarioService from "services/UsuarioService";
import CustomPagination from "components/CustomPagination";
import IconButton from "components/IconButton";
import { maskPhone } from "services/Utils";
import comboService from "services/ComboService";
import { PapelEnum } from "services/PerfilService";
import AppContext from "AppContext";

export default function Usuarios() {
  const { state } = useContext(AppContext)
  const idTipoPerfil = state?.user?.perfil?.idTipo

  const [pagina, setPagina] = useState(1)
  const [paginas, setPaginas] = useState(0)

  const [perfis, setPerfis] = useState([])
  const [empresas, setEmpresas] = useState([])

  const [termo, setTermo] = useState(null)
  const [idPerfil, setIdPerfil] = useState(null)
  const [idEmpresa, setIdEmpresa] = useState(null)

  const [items, setItems] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    comboService.perfil().then(res => {
      if (res.success) {
        setPerfis(res.data)
      }
    })

    if (idTipoPerfil == PapelEnum.ADMIN) {
      comboService.empresa().then(res => {
        if (res.success) {
          setEmpresas(res.data)
        }
      })
    }
  }, [])

  // Só EXECUTOR/GESTOR_EMPRESA tem empresa (mesma regra de escopo do backend) — pro Engenheiro,
  // filtrar por outro tipo de perfil nunca vai trazer nenhum registro da própria empresa.
  const perfisElegiveis = useMemo(() => {
    if (idTipoPerfil == PapelEnum.ADMIN)
      return perfis
    return perfis.filter(el => el.idTipo == PapelEnum.EXECUTOR || el.idTipo == PapelEnum.GESTOR_EMPRESA)
  }, [perfis, idTipoPerfil])

  const carregar = async () => {
    const res = await usuarioService.get(termo, idPerfil, idEmpresa, pagina)
    if (res.success) {
      setItems(res.data.items)
      setPaginas(res.data.paginas)
    }
  }

  useEffect(() => {
    carregar()
  }, [pagina])

  const handleDelete = (item) => {
    confirm({
      header: "Exclusão",
      text: "Tem certeza que gostaria de continuar?",
      icon: "bi-exclamation-triangle",
      onConfirm: async () => {
        try {
          const res = await usuarioService.delete(item.id)
          if (res.success) {
            carregar()
            toast("Usuário excluído")
          } else {
            toast("Erro ao excluir o usuário")
          }
        } catch (ex) {
          toast("Erro ao excluir o usuário")
        }
      }
    })
  }

  const handleSubmit = e => {
    e.preventDefault()
    setPagina(1)
    carregar()
  }

  return (
    <>
      <Container fluid>
        <div className="d-flex flex-row justify-content-between align-items-center">
          <h1 className="my-4">Usuários</h1>
          <Link className="btn btn-primary btn-icon" to="/admin/usuarios/0">Incluir</Link>
        </div>
        <Card className="shadow mb-4">
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col>
                  <FormGroup>
                    <Label>Nome ou Email</Label>
                    <Input type="text" placeholder="Nome ou Email" value={termo || ''} onChange={e => setTermo(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <Label>Perfil</Label>
                    <select className="form-control" value={idPerfil || ''} onChange={e => setIdPerfil(e.target.value ? +e.target.value : null)}>
                      <option value="">Todos</option>
                      {perfisElegiveis.map(el => (
                        <option key={el.id} value={el.id}>{el.nome}</option>
                      ))}
                    </select>
                  </FormGroup>
                </Col>
                {idTipoPerfil == PapelEnum.ADMIN && (
                  <Col>
                    <FormGroup>
                      <Label>Empresa</Label>
                      <select className="form-control" value={idEmpresa || ''} onChange={e => setIdEmpresa(e.target.value ? +e.target.value : null)}>
                        <option value="">Todas</option>
                        {empresas.map(el => (
                          <option key={el.id} value={el.id}>{el.razaoSocial}</option>
                        ))}
                      </select>
                    </FormGroup>
                  </Col>
                )}
                <Col className="d-flex align-items-end justify-content-end">
                  <FormGroup>
                    <Button type="submit" color="primary">Buscar</Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
        <Card className="shadow">
          <Table className="align-items-center table-flush" responsive>
            <thead className="thead-light">
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">E-mail</th>
                <th scope="col">Telefone</th>
                <th scope="col">Papéis</th>
                <th scope="col" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.length == 0 && (
                <tr>
                  <td colSpan={5} className="text-center">Nenhum registro</td>
                </tr>
              )}
              {items.map(el => (
                <tr key={el.id}>
                  <td>{el.nome}</td>
                  <td>{el.email}</td>
                  <td>{el.telefone && maskPhone(el.telefone)}</td>
                  <td>
                    {/* SDD 034 (Decisao 8) - lista de papeis do usuario (GET /usuario, ver
                    UsuarioService.formatarPapel na SDD 033), um badge por vinculo em vez de
                    uma coluna de texto unico */}
                    <div className="d-flex flex-wrap gap-5px">
                      {(el.papeis || []).map(p => (
                        <Badge key={p.id} color="info" pill>
                          {p.nomePerfil}
                          {p.razaoSocial ? ` — ${p.razaoSocial}` : ''}
                          {p.nomeSuperior ? ` — ${p.nomeSuperior}` : ''}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="d-flex flex-row gap-5px">
                    <IconButton alt="Editar" iconClass="bi-pencil-square" onClick={() => navigate("/admin/usuarios/" + el.id)} />
                    <IconButton alt="Excluir" iconClass="bi-trash" onClick={() => handleDelete(el)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <CardFooter className="py-4">
            <CustomPagination pagina={pagina} paginas={paginas} onChange={setPagina} />
          </CardFooter>
        </Card>
      </Container>
    </>
  )
}